import { useEffect, useState } from 'react';
import { plans as STATIC_PLANS, type Plan, type Cell } from '@/data/plans';
import { fetchPublicPlans, type PublicPlan } from '@/lib/data';

/* Single source of truth for how the app renders subscription plans.

   Prices + feature bullets + discount come from the DB (superadmin → Plans),
   read via the public_plans() RPC. Presentation-only metadata that isn't worth
   storing per row (marketing description, CTA label, "popular" flag, accent
   colour) is kept in the static catalog and merged in by tier. If the DB read
   returns nothing (RPC not deployed / offline) we fall back to the fully static
   catalog so the marketing site never renders empty. */

export interface DisplayPlan extends Plan {
  tier: string;
  /** Original list price (number). When > the selling price, show struck-through. */
  mrp: number | null;
  /** Numeric selling price (Plan.price is the formatted string). */
  priceValue: number;
}

const META = Object.fromEntries(
  STATIC_PLANS.map((p) => [
    p.name.toLowerCase(),
    { description: p.description, cta: p.cta, popular: p.popular, accent: p.accent, name: p.name },
  ]),
);

export function inr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function toDisplay(pp: PublicPlan): DisplayPlan {
  const meta = META[pp.tier.toLowerCase()] ?? {
    description: '',
    cta: 'Start 7-Day Free Trial',
    popular: pp.tier.toLowerCase() === 'growth',
    accent: 'border-background-200',
    name: pp.label,
  };
  return {
    tier: pp.tier,
    name: pp.label || meta.name,
    price: inr(pp.price),
    priceValue: pp.price,
    period: `/${pp.period}`,
    description: meta.description,
    features: pp.features,
    cta: meta.cta,
    popular: meta.popular,
    accent: meta.accent,
    mrp: pp.mrp && pp.mrp > pp.price ? pp.mrp : null,
  };
}

const STATIC_DISPLAY: DisplayPlan[] = STATIC_PLANS.map((p) => ({
  ...p,
  tier: p.name.toLowerCase(),
  mrp: null,
  priceValue: Number(p.price.replace(/[^\d.]/g, '')) || 0,
}));

export async function loadDisplayPlans(): Promise<DisplayPlan[]> {
  const db = await fetchPublicPlans();
  if (db.length === 0) return STATIC_DISPLAY;
  return db.map(toDisplay);
}

/** Discount % for a plan (0 when no valid MRP). */
export function discountPct(p: DisplayPlan): number {
  if (!p.mrp || p.mrp <= p.priceValue) return 0;
  return Math.round(((p.mrp - p.priceValue) / p.mrp) * 100);
}

/** Derive the /pricing comparison matrix from each plan's features. Higher-rank
    plans inherit lower plans' features (they carry an "Everything in X" bullet,
    which is treated as meta and dropped from the row list). */
export function buildComparison(plans: DisplayPlan[]): { feature: string; tiers: Cell[] }[] {
  const isMeta = (f: string) => /^everything in /i.test(f.trim());
  const cumulative: Set<string>[] = [];
  const order: string[] = [];
  const seen = new Set<string>();
  plans.forEach((p, idx) => {
    const set = new Set<string>(idx > 0 ? cumulative[idx - 1] : []);
    for (const f of p.features) {
      const t = f.trim();
      if (!t || isMeta(t)) continue;
      set.add(t);
      if (!seen.has(t)) { seen.add(t); order.push(t); }
    }
    cumulative.push(set);
  });
  return order.map((feature) => ({
    feature,
    tiers: plans.map((_, idx) => cumulative[idx].has(feature)) as Cell[],
  }));
}

/** React hook: starts with the static catalog, swaps in live DB plans on mount. */
export function usePlansCatalog(): { plans: DisplayPlan[]; loading: boolean } {
  const [plans, setPlans] = useState<DisplayPlan[]>(STATIC_DISPLAY);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    loadDisplayPlans()
      .then((p) => { if (alive) setPlans(p); })
      .catch(() => { /* keep static */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return { plans, loading };
}
